
import { Component, inject, input, OnInit, signal, ChangeDetectionStrategy } from '@angular/core';
import { Timestamp } from '@angular/fire/firestore';
import { FormBuilder, FormGroup, FormsModule, Validators } from '@angular/forms';
import { Dialog } from '@capacitor/dialog';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { LoadingButtonComponent, LoadingService, UploadService } from '@scandium-oy/ngx-scandium';
import { startOfToday } from 'date-fns';
import { take } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { MainDialogComponent } from 'src/app/components/main-dialog/main-dialog.component';
import { NoResultsComponent } from 'src/app/components/no-results/no-results.component';
import { SelectDayComponent } from 'src/app/components/select-day/select-day.component';
import { SpeechToTextComponent } from 'src/app/components/speech-to-text/speech-to-text.component';
import { SplitPageComponent } from 'src/app/components/split-page/split-page.component';
import { FileUpload } from 'src/app/models/file.model';
import { SiteContract } from 'src/app/models/site-contract.model';
import { Site } from 'src/app/models/site.model';
import { AiContract, AiContractTask, AiService } from 'src/app/services/ai.service';
import { ClientsService } from 'src/app/services/clients.service';
import { ContractorsService } from 'src/app/services/contractors.service';
import { SiteContractsService } from 'src/app/services/site-contracts.service';
import { WorkerTicketsService } from 'src/app/services/worker-tickets.service';
import { InviteUserDialogComponent } from 'src/app/users/invite/invite-user.dialog';
import { Roles } from 'src/app/utility/role';

@Component({
  selector: 'app-contract-dialog',
  templateUrl: './contract.dialog.html',
  styleUrls: ['./contract.dialog.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    AppCommonModule,
    MainDialogComponent,
    FormsModule,
    SplitPageComponent,
    SpeechToTextComponent,
    SelectDayComponent,
    LoadingButtonComponent,
    NoResultsComponent,
  ],
})
export class SiteContractDialogComponent implements OnInit {
  private aiService = inject(AiService);
  private clientsService = inject(ClientsService);
  private contractorsService = inject(ContractorsService);
  private formBuilder = inject(FormBuilder);
  private loadingService = inject(LoadingService);
  private _modal = inject(ModalController);
  private modalCtrl = inject(ModalController);
  private siteContractsService = inject(SiteContractsService);
  private translate = inject(TranslateService);
  private uploadService = inject(UploadService);
  private workerTicketsService = inject(WorkerTicketsService);

  site = input<Site>();

  contractUrls = signal<{ name: string; url: string }[]>([]);
  aiContract = signal<AiContract>(null);
  continousTasks = signal<AiContractTask[]>([]);
  additionalTasks = signal<AiContractTask[]>([]);
  tasks = signal<AiContractTask[]>([]);
  prompt = signal<string>('');
  step = signal(1);
  loading1 = signal(true);
  loading2 = signal(true);
  loading = signal(false);
  contract = signal<SiteContract>(null);
  formGroup: FormGroup;

  private setFormGroup() {
    const client = this.clientsService.getSync(this.site().client);

    this.formGroup = this.formBuilder.group({
      site: [this.site().name],
      client: [client?.name ?? ''],
      start: [startOfToday().toISOString(), Validators.required],
      end: [startOfToday().toISOString(), Validators.required],
      model: [''],
      area: [''],
    });
  }

  ngOnInit(): void {
    this.setFormGroup();
  }

  onDateChange(date: string, field: string) {
    this.formGroup.get(field).setValue(date);
  }

  async selectFiles(event: Event) {
    const inputElem = event.target as HTMLInputElement;
    this.loadingService.showLoading();
    for (const file of Array.from(inputElem.files)) {
      const fileupload = new FileUpload(file);
      const filename = file.name.split('.')[0];
      fileupload.guid = filename + '-' + new Date().toISOString();
      const downloadUrl = await this.uploadService.uploadImage(fileupload);
      this.contractUrls.update((val) => [...val, { name: filename, url: downloadUrl }]);
    }
    this.loadingService.hideLoading();
  }

  onMic(text: string) {
    this.prompt.update((val) => `${val} ${text}`);
  }

  removeDocument(item: { name: string; url: string }) {
    this.contractUrls.update((val) => val.filter((it) => it.name !== item.name && it.url !== item.url));
  }

  analyze() {
    this.step.set(3);
    this.loading1.set(true);
    this.loading2.set(true);
    setTimeout(() => this.loading1.set(false), 5 * 1000);
    setTimeout(() => this.loading2.set(false), 20 * 1000);
    this.aiService.createContract(this.contractUrls().map((it) => it.url), '', this.site().guid).pipe(
      take(1),
    ).subscribe({
      next: (res) => {
        if (res.error) {
          console.error(res.error);
          Dialog.alert({ message: this.translate.instant('tickets.ai.error') });
        } else {
          this.aiContract.set(res.contract);
          this.continousTasks.set(res.contract.tasks.filter((it) => it.continuous));
          this.additionalTasks.set(res.contract.tasks.filter((it) => it.additionalWork));
          this.tasks.set(res.contract.tasks.filter((it) => !it.continuous && !it.additionalWork));
          this.tasks().map((it) => {
            it.firstDate = this.formGroup.get('start').value;
          });
        }
      }, error: (error) => {
        console.error(error);
        Dialog.alert({ message: this.translate.instant('tickets.ai.error') });
      },
    });
  }

  checkContract() {
    this.step.set(4);
  }

  async save() {
    this.step.set(5);
    this.loading.set(true);
    const contractor = this.contractorsService.contractorS()?.guid;
    const { start, end, model, area } = this.formGroup.value;
    const startDate = Timestamp.fromDate(new Date(start));
    const endDate = new Date(end);
    const tickets = [];
    for (const task of this.tasks()) {
      const ticket = await this.workerTicketsService.createTicketFromContractAiTask(task, contractor, this.site(), endDate);
      tickets.push(...ticket.map((it) => ({ guid: it.guid, name: it.name, parent: it.parent, area: it.area })));
    }
    const item: SiteContract = {
      name: this.aiContract().name,
      site: this.site().guid,
      client: this.site().client ?? null,
      contractor,
      start: startDate,
      end: Timestamp.fromDate(endDate),
      model,
      area,
      tickets,
      generalNotes: this.continousTasks(),
      additionalWork: this.additionalTasks(),
      clientContacts: this.aiContract().clientContacts ?? [],
    };
    this.contract.set(item);
    this.siteContractsService.save(item).then(() => this.loading.set(false));
  }

  addContact() {
    this.modalCtrl.create({
      component: InviteUserDialogComponent,
      componentProps: { dummy: true, role: Roles.manager, site: this.site().guid }, cssClass: ['stack-modal'],
    }).then((m) => {
      m.present();

      m.onDidDismiss().then((data) => {
        if (data.data) {
          if (this.aiContract().clientContacts == null) {
            this.aiContract().clientContacts = [];
          }
          this.aiContract().clientContacts.push({ name: data.data.displayName, email: data.data.email, phone: data.data.phone, type: data.data.role });
        }
      });
    });
  }

  close() {
    this._modal.dismiss(this.contract());
  }

  dismiss() {
    this._modal.dismiss();
  }

}
